import { ValueGetterParams } from 'ag-grid-community';
import { memoize } from 'lodash';
import { AssigneeValue, FieldType } from 'components/organisms/tasks/fields';
import { Field } from 'stores/fields';
import { byAssigneeValue } from 'utils/comparators/by-assignee-value';
import { unreachable } from 'utils/unreachable';

type Nullable<T> = T | null;

interface ValueGetter {
  (params: ValueGetterParams): any;
}

const memoizeNumberArray = memoize(
  (value: Nullable<number[]>) => value,
  (value: Nullable<number[]>) =>
    value == null ? 'null' : value.slice().sort().join(','),
);

const memoizeAssigneeValue = memoize(
  (value: AssigneeValue[] = []) => value,
  (value: AssigneeValue[] = []) =>
    value
      .filter(Boolean)
      .sort(byAssigneeValue)
      .map(AssigneeValue.toString)
      .join(','),
);

export const getBaseValueGetter = (
  field: Pick<Field, 'type' | 'fieldName'>,
): ValueGetter | undefined => {
  if (field.fieldName === 'lastStatusUpdate') {
    return ({ data }) => {
      const byUpdatedAt = (status) => status.updatedAt;
      const updates = data?.pastProjectStatus?.map(byUpdatedAt) ?? [];

      return updates.length ? Math.max(...updates) : null;
    };
  }
  switch (field.type) {
    case FieldType.Estimate:
    case FieldType.Number:
    case FieldType.Date:
    case FieldType.Text:
    case FieldType.MultiLineText:
    case FieldType.Boolean:
    case FieldType.SingleSelect:
    case FieldType.SingleUser:
    case FieldType.Note:
      return undefined;
    case FieldType.MultiSelect:
      return ({ data }) => memoizeNumberArray(data?.[field.fieldName]);
    case FieldType.MultiUser:
      return ({ data }) => memoizeNumberArray(data?.[field.fieldName]);
    case FieldType.Assignee:
      return ({ data }) => memoizeAssigneeValue(data?.[field.fieldName]);
    default:
      return unreachable(field.type);
  }
};
